import type { LyricInput } from '@creanizer/types'
import { Label } from '@creanizer/ui'
import { Input } from '@creanizer/ui'
import { Textarea } from '@creanizer/ui'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import { Checkbox } from '@creanizer/ui'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@creanizer/ui'
import { Slider } from '@creanizer/ui'
import { Card } from '@creanizer/ui'
import { ScrollArea } from '@creanizer/ui'
import { Separator } from '@creanizer/ui'
import { Badge } from '@creanizer/ui'
import { Button } from '@creanizer/ui'
import { Plus, X, Upload, Globe } from 'lucide-react'
import { useState } from 'react'
import * as mammoth from 'mammoth'
import { useWorldBuilderStore } from '../../../../stores/worldbuilderStore'
import { useCharacterStore } from '../../../../stores/characterStore'
import { generateWorldContextText, generateCharacterContext } from '@creanizer/utils'
import { Users } from 'lucide-react'

interface LyricFormDetailedProps {
  input: LyricInput
  onChange: (input: LyricInput) => void
}

export function LyricFormDetailed({ input, onChange }: LyricFormDetailedProps) {
  const [newKeyword, setNewKeyword] = useState('')
  const [newForbiddenWord, setNewForbiddenWord] = useState('')
  const [newPreferredWord, setNewPreferredWord] = useState('')
  const [selectedWorldId, setSelectedWorldId] = useState<string>('')
  
  const { savedWorlds } = useWorldBuilderStore()
  const { characters: savedCharacters } = useCharacterStore()

  const handleChange = (field: keyof LyricInput, value: any) => {
    onChange({ ...input, [field]: value })
  }

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files
    if (!files) return

    for (const file of Array.from(files)) {
      let content = ''
      
      if (file.name.endsWith('.docx')) {
        const arrayBuffer = await file.arrayBuffer()
        const result = await mammoth.extractRawText({ arrayBuffer })
        content = result.value
      } else {
        content = await file.text()
      }
      
      const newFile = {
        content,
        fileName: file.name,
        fileType: 'auto' as const,
        weight: 1.0
      }
      const uploadedFiles = input.uploadedFiles || []
      handleChange('uploadedFiles', [...uploadedFiles, newFile])
    }
  }

  const removeFile = (index: number) => {
    const files = input.uploadedFiles || []
    handleChange('uploadedFiles', files.filter((_, i) => i !== index))
  }

  const addKeyword = (type: 'mustInclude' | 'forbidden' | 'preferred') => {
    const keywordControl = input.keywordControl || {}
    
    if (type === 'mustInclude' && newKeyword) {
      const current = keywordControl.mustInclude || []
      handleChange('keywordControl', {
        ...keywordControl,
        mustInclude: [...current, { word: newKeyword, frequency: 'auto' }]
      })
      setNewKeyword('')
    } else if (type === 'forbidden' && newForbiddenWord) {
      const current = keywordControl.forbidden || []
      handleChange('keywordControl', {
        ...keywordControl,
        forbidden: [...current, newForbiddenWord]
      })
      setNewForbiddenWord('')
    } else if (type === 'preferred' && newPreferredWord) {
      const current = keywordControl.preferred || []
      handleChange('keywordControl', {
        ...keywordControl,
        preferred: [...current, newPreferredWord]
      })
      setNewPreferredWord('')
    }
  }

  const removeKeyword = (type: 'mustInclude' | 'forbidden' | 'preferred', index: number) => {
    const keywordControl = input.keywordControl || {}
    
    if (type === 'mustInclude') {
      const current = keywordControl.mustInclude || []
      handleChange('keywordControl', {
        ...keywordControl,
        mustInclude: current.filter((_, i) => i !== index)
      })
    } else if (type === 'forbidden') {
      const current = keywordControl.forbidden || []
      handleChange('keywordControl', {
        ...keywordControl,
        forbidden: current.filter((_, i) => i !== index)
      })
    } else if (type === 'preferred') {
      const current = keywordControl.preferred || []
      handleChange('keywordControl', {
        ...keywordControl,
        preferred: current.filter((_, i) => i !== index)
      })
    }
  }

  return (
    <Tabs defaultValue="basic" className="w-full">
      <TabsList className="grid grid-cols-3 lg:grid-cols-6 w-full">
        <TabsTrigger value="basic">基本</TabsTrigger>
        <TabsTrigger value="files">ファイル</TabsTrigger>
        <TabsTrigger value="music">音楽</TabsTrigger>
        <TabsTrigger value="style">スタイル</TabsTrigger>
        <TabsTrigger value="keywords">キーワード</TabsTrigger>
        <TabsTrigger value="advanced">高度な設定</TabsTrigger>
      </TabsList>

      {/* 基本設定タブ */}
      <TabsContent value="basic" className="space-y-4 mt-4">
        <ScrollArea className="h-[500px] pr-4">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="title">曲タイトル（オプション）</Label>
                <Input
                  id="title"
                  placeholder="例：夏の思い出"
                  value={input.title || ''}
                  onChange={(e) => handleChange('title', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label>ジャンル（複数選択可）</Label>
                {input.genre === 'auto' ? (
                  <div className="space-y-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => handleChange('genre', [])}
                      className="w-full"
                    >
                      手動でジャンルを選択
                    </Button>
                    <p className="text-sm text-muted-foreground">現在：自動選択</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="grid grid-cols-3 gap-2">
                      {[
                        { value: 'pop', label: 'ポップ' },
                        { value: 'rock', label: 'ロック' },
                        { value: 'hiphop', label: 'ヒップホップ' },
                        { value: 'ballad', label: 'バラード' },
                        { value: 'jpop', label: 'J-POP' },
                        { value: 'kpop', label: 'K-POP' },
                        { value: 'anime', label: 'アニメソング' },
                        { value: 'game', label: 'ゲーム音楽' },
                        { value: 'vocaloid', label: 'ボーカロイド' },
                        { value: 'rnb', label: 'R&B' },
                        { value: 'jazz', label: 'ジャズ' },
                        { value: 'electronic', label: 'エレクトロニック' },
                        { value: 'metal', label: 'メタル' },
                        { value: 'folk', label: 'フォーク' },
                        { value: 'soul', label: 'ソウル' }
                      ].map(genre => (
                        <div key={genre.value} className="flex items-center space-x-2">
                          <Checkbox
                            id={`genre-${genre.value}`}
                            checked={Array.isArray(input.genre) && input.genre.includes(genre.value as any)}
                            onCheckedChange={(checked) => {
                              const currentGenres = Array.isArray(input.genre) ? input.genre : []
                              if (checked) {
                                handleChange('genre', [...currentGenres, genre.value])
                              } else {
                                handleChange('genre', currentGenres.filter(g => g !== genre.value))
                              }
                            }}
                          />
                          <Label htmlFor={`genre-${genre.value}`} className="text-sm">
                            {genre.label}
                          </Label>
                        </div>
                      ))}
                    </div>
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleChange('genre', 'auto')}
                      >
                        自動選択に戻す
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => handleChange('genre', 'custom')}
                      >
                        カスタムジャンル
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {(input.genre === 'custom' || (Array.isArray(input.genre) && input.genre.length === 0)) && (
              <div className="space-y-2">
                <Label htmlFor="customGenre">カスタムジャンル</Label>
                <Input
                  id="customGenre"
                  placeholder="ジャンルを入力"
                  value={input.customGenre || ''}
                  onChange={(e) => handleChange('customGenre', e.target.value)}
                />
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="language">言語</Label>
                <Select 
                  value={input.language || 'auto'} 
                  onValueChange={(value) => handleChange('language', value)}
                >
                  <SelectTrigger id="language">
                    <SelectValue placeholder="言語を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="japanese">日本語</SelectItem>
                    <SelectItem value="english">英語</SelectItem>
                    <SelectItem value="mixed">日英混合</SelectItem>
                    <SelectItem value="korean">韓国語</SelectItem>
                    <SelectItem value="other">その他</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="mood">雰囲気</Label>
                <Select 
                  value={input.mood || 'auto'} 
                  onValueChange={(value) => handleChange('mood', value)}
                >
                  <SelectTrigger id="mood">
                    <SelectValue placeholder="雰囲気を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="happy">楽しい</SelectItem>
                    <SelectItem value="sad">悲しい</SelectItem>
                    <SelectItem value="energetic">エネルギッシュ</SelectItem>
                    <SelectItem value="romantic">ロマンチック</SelectItem>
                    <SelectItem value="nostalgic">ノスタルジック</SelectItem>
                    <SelectItem value="hopeful">希望的</SelectItem>
                    <SelectItem value="mysterious">神秘的</SelectItem>
                    <SelectItem value="dark">ダーク</SelectItem>
                    <SelectItem value="bright">明るい</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {input.language === 'mixed' && (
              <div className="space-y-4">
                <Label>言語ミックス設定</Label>
                <div className="grid grid-cols-3 gap-2">
                  <Select 
                    value={input.languageMix?.primary || 'japanese'}
                    onValueChange={(value) => handleChange('languageMix', {...input.languageMix, primary: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="メイン言語" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="japanese">日本語</SelectItem>
                      <SelectItem value="english">英語</SelectItem>
                      <SelectItem value="korean">韓国語</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select 
                    value={input.languageMix?.secondary || 'english'}
                    onValueChange={(value) => handleChange('languageMix', {...input.languageMix, secondary: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="サブ言語" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="english">英語</SelectItem>
                      <SelectItem value="japanese">日本語</SelectItem>
                      <SelectItem value="korean">韓国語</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select 
                    value={input.languageMix?.ratio || 'auto'}
                    onValueChange={(value) => handleChange('languageMix', {...input.languageMix, ratio: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="比率" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">自動</SelectItem>
                      <SelectItem value="70:30">70:30</SelectItem>
                      <SelectItem value="50:50">50:50</SelectItem>
                      <SelectItem value="30:70">30:70</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <Select 
                  value={input.languageMix?.mixPattern || 'auto'}
                  onValueChange={(value) => handleChange('languageMix', {...input.languageMix, mixPattern: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="ミックスパターン" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動</SelectItem>
                    <SelectItem value="alternating">交互</SelectItem>
                    <SelectItem value="chorus_only">コーラスのみ</SelectItem>
                    <SelectItem value="random">ランダム</SelectItem>
                    <SelectItem value="specific">部分指定</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="theme">メインテーマ</Label>
              <Textarea
                id="theme"
                placeholder="歌詞のメインテーマを入力（例：青春時代の友情、失恋からの立ち直り、夢への挑戦）"
                value={input.theme || ''}
                onChange={(e) => handleChange('theme', e.target.value)}
                className="min-h-[100px]"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="additionalInstructions">追加の指示</Label>
              <Textarea
                id="additionalInstructions"
                placeholder="その他の要望や具体的な指示があれば入力してください"
                value={input.additionalInstructions || ''}
                onChange={(e) => handleChange('additionalInstructions', e.target.value)}
                className="min-h-[80px]"
              />
            </div>

            <div className="space-y-2">
              <Label>雰囲気詳細</Label>
              <div className="grid grid-cols-3 gap-2">
                <Select 
                  value={input.atmosphere?.season || 'auto'} 
                  onValueChange={(value) => handleChange('atmosphere', {...input.atmosphere, season: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="季節" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="春">春</SelectItem>
                    <SelectItem value="夏">夏</SelectItem>
                    <SelectItem value="秋">秋</SelectItem>
                    <SelectItem value="冬">冬</SelectItem>
                    <SelectItem value="なし">なし</SelectItem>
                  </SelectContent>
                </Select>

                <Select 
                  value={input.atmosphere?.timeOfDay || 'auto'} 
                  onValueChange={(value) => handleChange('atmosphere', {...input.atmosphere, timeOfDay: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="時間帯" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="朝">朝</SelectItem>
                    <SelectItem value="昼">昼</SelectItem>
                    <SelectItem value="夕暮れ">夕暮れ</SelectItem>
                    <SelectItem value="夜">夜</SelectItem>
                    <SelectItem value="深夜">深夜</SelectItem>
                  </SelectContent>
                </Select>

                <Select 
                  value={input.atmosphere?.weather || 'auto'} 
                  onValueChange={(value) => handleChange('atmosphere', {...input.atmosphere, weather: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="天気" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="晴れ">晴れ</SelectItem>
                    <SelectItem value="雨">雨</SelectItem>
                    <SelectItem value="雪">雪</SelectItem>
                    <SelectItem value="曇り">曇り</SelectItem>
                    <SelectItem value="嵐">嵐</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Input
                placeholder="場所（例：海辺、都市、山間部）"
                value={input.atmosphere?.location || ''}
                onChange={(e) => handleChange('atmosphere', {...input.atmosphere, location: e.target.value})}
              />
            </div>

            {savedWorlds.length > 0 && (
              <div className="space-y-2">
                <Label htmlFor="world">世界観を選択（オプション）</Label>
                <Select 
                  value={selectedWorldId || 'none'} 
                  onValueChange={(value) => {
                    if (value === 'none') {
                      setSelectedWorldId('')
                      handleChange('worldContext', undefined)
                    } else {
                      setSelectedWorldId(value)
                      const world = savedWorlds.find(w => w.id === value)
                      if (world) {
                        const worldContext = generateWorldContextText(world.result)
                        handleChange('worldContext', worldContext)
                      }
                    }
                  }}
                >
                  <SelectTrigger id="world">
                    <SelectValue placeholder="世界観を選択...">
                      {selectedWorldId ? (
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          {savedWorlds.find(w => w.id === selectedWorldId)?.title}
                        </div>
                      ) : (
                        '世界観を選択...'
                      )}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">選択しない</SelectItem>
                    {savedWorlds.map((world) => (
                      <SelectItem key={world.id} value={world.id}>
                        <div className="flex items-center gap-2">
                          <Globe className="h-4 w-4" />
                          {world.title}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {selectedWorldId && (
                  <div className="mt-2 p-2 bg-blue-50 rounded text-xs">
                    <div className="font-medium text-blue-900 mb-1">選択中の世界観</div>
                    <div className="text-blue-700">
                      {savedWorlds.find(w => w.id === selectedWorldId)?.result.summary?.substring(0, 100)}...
                    </div>
                    <div className="text-blue-600 mt-1">
                      ※ この世界観のテーマや雰囲気に合った歌詞が生成されます
                    </div>
                  </div>
                )}
              </div>
            )}

            {savedCharacters.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4" />
                  <Label>キャラクターを選択（オプション）</Label>
                </div>
                
                <Select 
                  value={input.characterUsage || 'auto'} 
                  onValueChange={(value) => handleChange('characterUsage', value)}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="キャラクターとの関係性" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択（AIが最適な関係性を判断）</SelectItem>
                    <SelectItem value="theme_song">イメージソング（キャラクターのテーマ曲）</SelectItem>
                    <SelectItem value="written_by">キャラクターが書いた歌詞</SelectItem>
                    <SelectItem value="related_to">キャラクターに関係する内容</SelectItem>
                    <SelectItem value="perspective_of">キャラクターの感性で作成</SelectItem>
                    <SelectItem value="multiple">複数の関係性を組み合わせ</SelectItem>
                  </SelectContent>
                </Select>

                <div className="grid grid-cols-2 gap-2 max-h-60 overflow-y-auto border rounded-md p-2">
                  {savedCharacters.map((character) => {
                    const checkboxId = `character-checkbox-${character.id}`
                    return (
                      <div
                        key={character.id}
                        className="flex items-center space-x-2 p-2 border rounded hover:bg-accent transition-colors"
                      >
                        <input
                          id={checkboxId}
                          type="checkbox"
                          checked={input.characterIds?.includes(character.id) || false}
                          onChange={(e) => {
                            const ids = input.characterIds || []
                            const newIds = e.target.checked
                              ? [...ids, character.id]
                              : ids.filter(id => id !== character.id)
                            
                            // デバッグログ
                            console.log('[LyricForm] Character selection changed:', {
                              characterId: character.id,
                              checked: e.target.checked,
                              oldIds: ids,
                              newIds: newIds
                            })
                            
                            // キャラクターコンテキストを生成
                            let newContext: string | undefined = undefined
                            if (newIds.length > 0) {
                              const selectedChars = savedCharacters.filter(c => newIds.includes(c.id))
                              const contexts = selectedChars.map(char => generateCharacterContext(char))
                              newContext = contexts.join('\n\n')
                            }
                            
                            // 一度にまとめて更新
                            onChange({
                              ...input,
                              characterIds: newIds,
                              characterContext: newContext
                            })
                          }}
                          className="rounded"
                        />
                        <label 
                          htmlFor={checkboxId}
                          className="text-sm truncate cursor-pointer flex-1" 
                          title={character.title}
                        >
                          {character.title}
                        </label>
                      </div>
                    )
                  })}
                </div>
                
                {input.characterIds && input.characterIds.length > 0 && (
                  <div className="mt-2 p-2 bg-purple-50 rounded text-xs">
                    <div className="font-medium text-purple-900 mb-1">
                      選択中: {input.characterIds.length}名のキャラクター
                    </div>
                    <div className="text-purple-700">
                      {input.characterUsage === 'theme_song' && '選択したキャラクターのイメージソングを作成します'}
                      {input.characterUsage === 'written_by' && '選択したキャラクターが書いた設定の歌詞を作成します'}
                      {input.characterUsage === 'related_to' && '選択したキャラクターと関係の深い内容の歌詞を作成します'}
                      {input.characterUsage === 'perspective_of' && '選択したキャラクターの感性で歌詞を作成します'}
                      {input.characterUsage === 'multiple' && '複数の関係性を組み合わせて歌詞を作成します'}
                      {input.characterUsage === 'auto' && 'AIが最適な関係性を判断して歌詞を作成します'}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      </TabsContent>

      {/* ファイルタブ */}
      <TabsContent value="files" className="space-y-4 mt-4">
        <ScrollArea className="h-[500px] pr-4">
          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="file-upload">参考ファイル（.txt, .md, .docx）</Label>
              <div className="flex items-center gap-2">
                <Input
                  id="file-upload"
                  type="file"
                  accept=".txt,.md,.docx"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('file-upload')?.click()}
                >
                  <Upload className="mr-2 h-4 w-4" />
                  ファイルを選択
                </Button>
              </div>
            </div>

            {input.uploadedFiles && input.uploadedFiles.length > 0 && (
              <div className="space-y-2">
                {input.uploadedFiles.map((file, index) => (
                  <div key={index} className="flex items-center justify-between p-2 bg-muted rounded">
                    <span className="text-sm">{file.fileName}</span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => removeFile(index)}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="freeformInput">補足説明</Label>
              <Textarea
                id="freeformInput"
                placeholder="アップロードしたファイルについての補足説明や、どう使ってほしいかの指示"
                value={input.freeformInput?.text || ''}
                onChange={(e) => handleChange('freeformInput', { text: e.target.value, purpose: 'supplement' })}
                className="min-h-[120px]"
              />
            </div>
          </div>
        </ScrollArea>
      </TabsContent>

      {/* 音楽設定タブ */}
      <TabsContent value="music" className="space-y-4 mt-4">
        <ScrollArea className="h-[500px] pr-4">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bpm">BPM</Label>
                <Input
                  id="bpm"
                  type="number"
                  min="60"
                  max="200"
                  placeholder="自動"
                  value={typeof input.bpm === 'number' ? input.bpm : ''}
                  onChange={(e) => handleChange('bpm', e.target.value ? parseInt(e.target.value) : 'auto')}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="timeSignature">拍子</Label>
                <Select 
                  value={input.timeSignature || 'auto'} 
                  onValueChange={(value) => handleChange('timeSignature', value)}
                >
                  <SelectTrigger id="timeSignature">
                    <SelectValue placeholder="拍子を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動</SelectItem>
                    <SelectItem value="4/4">4/4</SelectItem>
                    <SelectItem value="3/4">3/4</SelectItem>
                    <SelectItem value="6/8">6/8</SelectItem>
                    <SelectItem value="other">その他</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="structure">曲構造</Label>
                <Select 
                  value={input.structure || 'auto'} 
                  onValueChange={(value) => handleChange('structure', value)}
                >
                  <SelectTrigger id="structure">
                    <SelectValue placeholder="構造を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="verse_chorus">ヴァース・コーラス</SelectItem>
                    <SelectItem value="verse_prechorus_chorus_bridge">ヴァース・プレコーラス・コーラス・ブリッジ</SelectItem>
                    <SelectItem value="aaba">AABA形式</SelectItem>
                    <SelectItem value="verse_only">ヴァースのみ</SelectItem>
                    <SelectItem value="free_form">自由形式</SelectItem>
                    <SelectItem value="custom">カスタム</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="songLength">曲の長さ</Label>
                <Select 
                  value={input.songLength || 'auto'} 
                  onValueChange={(value) => handleChange('songLength', value)}
                >
                  <SelectTrigger id="songLength">
                    <SelectValue placeholder="長さを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="short">短め（2-3分）</SelectItem>
                    <SelectItem value="medium">標準（3-4分）</SelectItem>
                    <SelectItem value="long">長め（4分以上）</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label>想定楽器</Label>
              <div className="flex flex-wrap gap-2">
                {['piano', 'guitar', 'drums', 'bass', 'strings', 'synthesizer', 'vocal'].map(instrument => (
                  <Button
                    key={instrument}
                    type="button"
                    variant={input.instruments?.includes(instrument) ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => {
                      const current = input.instruments || []
                      handleChange('instruments', 
                        current.includes(instrument) 
                          ? current.filter(i => i !== instrument)
                          : [...current, instrument]
                      )
                    }}
                  >
                    {instrument === 'piano' && 'ピアノ'}
                    {instrument === 'guitar' && 'ギター'}
                    {instrument === 'drums' && 'ドラム'}
                    {instrument === 'bass' && 'ベース'}
                    {instrument === 'strings' && '弦楽器'}
                    {instrument === 'synthesizer' && 'シンセ'}
                    {instrument === 'vocal' && 'ボーカル'}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="keySignature">キー（調性）</Label>
                <Input
                  id="keySignature"
                  placeholder="例：C major, A minor"
                  value={input.keySignature || ''}
                  onChange={(e) => handleChange('keySignature', e.target.value)}
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="tempo">テンポ</Label>
                <Select 
                  value={input.tempo || 'auto'} 
                  onValueChange={(value) => handleChange('tempo', value)}
                >
                  <SelectTrigger id="tempo">
                    <SelectValue placeholder="テンポを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動</SelectItem>
                    <SelectItem value="slow">遅い</SelectItem>
                    <SelectItem value="medium">中速</SelectItem>
                    <SelectItem value="fast">速い</SelectItem>
                    <SelectItem value="variable">可変</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="musicalStyle">音楽スタイル</Label>
              <Select 
                value={input.musicalStyle || 'auto'} 
                onValueChange={(value) => handleChange('musicalStyle', value)}
              >
                <SelectTrigger id="musicalStyle">
                  <SelectValue placeholder="スタイルを選択" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">自動</SelectItem>
                  <SelectItem value="acoustic">アコースティック</SelectItem>
                  <SelectItem value="electronic">エレクトロニック</SelectItem>
                  <SelectItem value="orchestral">オーケストラ</SelectItem>
                  <SelectItem value="band">バンド</SelectItem>
                  <SelectItem value="minimal">ミニマル</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </ScrollArea>
      </TabsContent>

      {/* スタイルタブ */}
      <TabsContent value="style" className="space-y-4 mt-4">
        <ScrollArea className="h-[500px] pr-4">
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="writingStyle">文体</Label>
                <Select 
                  value={input.writingStyle || 'auto'} 
                  onValueChange={(value) => handleChange('writingStyle', value)}
                >
                  <SelectTrigger id="writingStyle">
                    <SelectValue placeholder="文体を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="poetic">詩的</SelectItem>
                    <SelectItem value="conversational">会話的</SelectItem>
                    <SelectItem value="narrative">物語的</SelectItem>
                    <SelectItem value="abstract">抽象的</SelectItem>
                    <SelectItem value="direct">直接的</SelectItem>
                    <SelectItem value="metaphorical">比喩的</SelectItem>
                    <SelectItem value="ambiguous_japanese">曖昧な日本語（主語・接続語省略）</SelectItem>
                    <SelectItem value="stream_of_consciousness">意識の流れ</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="perspective">視点</Label>
                <Select 
                  value={input.perspective || 'auto'} 
                  onValueChange={(value) => handleChange('perspective', value)}
                >
                  <SelectTrigger id="perspective">
                    <SelectValue placeholder="視点を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="first_person">一人称</SelectItem>
                    <SelectItem value="second_person">二人称</SelectItem>
                    <SelectItem value="third_person">三人称</SelectItem>
                    <SelectItem value="mixed">混合</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="tense">時制</Label>
                <Select 
                  value={input.tense || 'auto'} 
                  onValueChange={(value) => handleChange('tense', value)}
                >
                  <SelectTrigger id="tense">
                    <SelectValue placeholder="時制を選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="past">過去</SelectItem>
                    <SelectItem value="present">現在</SelectItem>
                    <SelectItem value="future">未来</SelectItem>
                    <SelectItem value="mixed">混合</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="vocabularyLevel">語彙レベル</Label>
                <Select 
                  value={input.vocabularyLevel || 'auto'} 
                  onValueChange={(value) => handleChange('vocabularyLevel', value)}
                >
                  <SelectTrigger id="vocabularyLevel">
                    <SelectValue placeholder="語彙レベルを選択" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="simple">シンプル</SelectItem>
                    <SelectItem value="standard">標準</SelectItem>
                    <SelectItem value="complex">複雑</SelectItem>
                    <SelectItem value="mixed">混合</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>韻・リズム設定</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select 
                  value={input.rhymeSettings?.rhymeScheme || 'auto'} 
                  onValueChange={(value) => handleChange('rhymeSettings', {...input.rhymeSettings, rhymeScheme: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="韻の構成" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="aabb">AABB（連韻）</SelectItem>
                    <SelectItem value="abab">ABAB（交差韻）</SelectItem>
                    <SelectItem value="abcb">ABCB</SelectItem>
                    <SelectItem value="free">自由形式</SelectItem>
                    <SelectItem value="none">韻なし</SelectItem>
                    <SelectItem value="custom">カスタム</SelectItem>
                  </SelectContent>
                </Select>
                <Select 
                  value={input.rhymeSettings?.rhymeDensity || 'auto'} 
                  onValueChange={(value) => handleChange('rhymeSettings', {...input.rhymeSettings, rhymeDensity: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="韻の密度" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="sparse">疎</SelectItem>
                    <SelectItem value="moderate">中程度</SelectItem>
                    <SelectItem value="dense">密</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <Select 
                  value={input.rhymeSettings?.rhymeType || 'auto'} 
                  onValueChange={(value) => handleChange('rhymeSettings', {...input.rhymeSettings, rhymeType: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="韻のタイプ" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="perfect">完全韻</SelectItem>
                    <SelectItem value="slant">不完全韻</SelectItem>
                    <SelectItem value="internal">内部韻</SelectItem>
                    <SelectItem value="multi">多重韻</SelectItem>
                    <SelectItem value="none">なし</SelectItem>
                  </SelectContent>
                </Select>
                <Select 
                  value={input.rhymeSettings?.rhymePosition || 'auto'} 
                  onValueChange={(value) => handleChange('rhymeSettings', {...input.rhymeSettings, rhymePosition: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="韻の位置" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="line_end">行末</SelectItem>
                    <SelectItem value="mid_line">行中</SelectItem>
                    <SelectItem value="both">両方</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {input.rhymeSettings?.rhymeScheme === 'custom' && (
                <Input
                  placeholder="カスタム韻パターン（例：ABABCDCD）"
                  value={input.rhymeSettings?.customRhymeScheme || ''}
                  onChange={(e) => handleChange('rhymeSettings', {...input.rhymeSettings, customRhymeScheme: e.target.value})}
                />
              )}
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>日本語スタイル設定</Label>
              <div className="space-y-3">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="omitSubject"
                    checked={input.japaneseStyleOptions?.omitSubject || false}
                    onCheckedChange={(checked) => handleChange('japaneseStyleOptions', {...input.japaneseStyleOptions, omitSubject: checked as boolean})}
                  />
                  <Label htmlFor="omitSubject" className="text-sm">
                    主語を省略
                    <span className="text-xs text-muted-foreground ml-2">（「僕は」「君が」などを省く）</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="omitConnectives"
                    checked={input.japaneseStyleOptions?.omitConnectives || false}
                    onCheckedChange={(checked) => handleChange('japaneseStyleOptions', {...input.japaneseStyleOptions, omitConnectives: checked as boolean})}
                  />
                  <Label htmlFor="omitConnectives" className="text-sm">
                    接続語を省略
                    <span className="text-xs text-muted-foreground ml-2">（「だから」「でも」などを省く）</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="ambiguousExpressions"
                    checked={input.japaneseStyleOptions?.ambiguousExpressions || false}
                    onCheckedChange={(checked) => handleChange('japaneseStyleOptions', {...input.japaneseStyleOptions, ambiguousExpressions: checked as boolean})}
                  />
                  <Label htmlFor="ambiguousExpressions" className="text-sm">
                    曖昧な表現を使用
                    <span className="text-xs text-muted-foreground ml-2">（断定を避け、余韻を残す）</span>
                  </Label>
                </div>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="poeticAmbiguity"
                    checked={input.japaneseStyleOptions?.poeticAmbiguity || false}
                    onCheckedChange={(checked) => handleChange('japaneseStyleOptions', {...input.japaneseStyleOptions, poeticAmbiguity: checked as boolean})}
                  />
                  <Label htmlFor="poeticAmbiguity" className="text-sm">
                    詩的曖昧性
                    <span className="text-xs text-muted-foreground ml-2">（解釈の余地を残す表現）</span>
                  </Label>
                </div>
              </div>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>フック設定</Label>
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="autoGenerateHook"
                  checked={input.hookSettings?.autoGenerateHook !== false}
                  onCheckedChange={(checked) => handleChange('hookSettings', {...input.hookSettings, autoGenerateHook: checked as boolean})}
                />
                <Label htmlFor="autoGenerateHook">自動でフックを生成</Label>
              </div>
              {input.hookSettings?.autoGenerateHook !== false && (
                <div className="grid grid-cols-2 gap-2 mt-2">
                  <Input
                    type="number"
                    placeholder="フック繰り返し回数"
                    value={input.hookSettings?.hookRepetition || ''}
                    onChange={(e) => handleChange('hookSettings', {...input.hookSettings, hookRepetition: e.target.value ? parseInt(e.target.value) : 'auto'})}
                  />
                  <Select 
                    value={input.hookSettings?.memorability || 'auto'} 
                    onValueChange={(value) => handleChange('hookSettings', {...input.hookSettings, memorability: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="覚えやすさ" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="auto">自動</SelectItem>
                      <SelectItem value="simple">シンプル</SelectItem>
                      <SelectItem value="moderate">適度</SelectItem>
                      <SelectItem value="complex">複雑</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <Separator />

            <div className="space-y-2">
              <Label htmlFor="referenceText">参照テキスト（スタイル参考用）</Label>
              <Textarea
                id="referenceText"
                placeholder="参考にしたい既存の歌詞を入力してください"
                value={input.referenceText || ''}
                onChange={(e) => handleChange('referenceText', e.target.value)}
                className="min-h-[120px]"
              />
            </div>
          </div>
        </ScrollArea>
      </TabsContent>

      {/* キーワード設定タブ */}
      <TabsContent value="keywords" className="space-y-4 mt-4">
        <ScrollArea className="h-[500px] pr-4">
          <div className="space-y-6">
            {/* 必須キーワード */}
            <div className="space-y-2">
              <Label>必須キーワード</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="必ず含めたい言葉"
                  value={newKeyword}
                  onChange={(e) => setNewKeyword(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addKeyword('mustInclude')
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addKeyword('mustInclude')}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {input.keywordControl?.mustInclude?.map((keyword, index) => (
                  <Badge key={index} variant="default">
                    {keyword.word}
                    <button
                      type="button"
                      onClick={() => removeKeyword('mustInclude', index)}
                      className="ml-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <Separator />

            {/* 禁止ワード */}
            <div className="space-y-2">
              <Label>禁止ワード</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="使用を避けたい言葉"
                  value={newForbiddenWord}
                  onChange={(e) => setNewForbiddenWord(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addKeyword('forbidden')
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addKeyword('forbidden')}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {input.keywordControl?.forbidden?.map((word, index) => (
                  <Badge key={index} variant="destructive">
                    {word}
                    <button
                      type="button"
                      onClick={() => removeKeyword('forbidden', index)}
                      className="ml-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <Separator />

            {/* 推奨ワード */}
            <div className="space-y-2">
              <Label>推奨ワード</Label>
              <div className="flex gap-2">
                <Input
                  placeholder="できれば含めたい言葉"
                  value={newPreferredWord}
                  onChange={(e) => setNewPreferredWord(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault()
                      addKeyword('preferred')
                    }
                  }}
                />
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => addKeyword('preferred')}
                >
                  <Plus className="h-4 w-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {input.keywordControl?.preferred?.map((word, index) => (
                  <Badge key={index} variant="secondary">
                    {word}
                    <button
                      type="button"
                      onClick={() => removeKeyword('preferred', index)}
                      className="ml-1"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>
          </div>
        </ScrollArea>
      </TabsContent>

      {/* 高度な設定タブ */}
      <TabsContent value="advanced" className="space-y-4 mt-4">
        <ScrollArea className="h-[500px] pr-4">
          <div className="space-y-6">
            <div className="space-y-2">
              <Label>比喩レベル（0-10）</Label>
              <Slider
                value={[typeof input.advancedSettings?.metaphorLevel === 'number' ? input.advancedSettings.metaphorLevel : 5]}
                onValueChange={([value]) => handleChange('advancedSettings', {...input.advancedSettings, metaphorLevel: value})}
                max={10}
                min={0}
                step={1}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>リテラル</span>
                <span>バランス</span>
                <span>抽象的</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label>詩的密度（0-10）</Label>
              <Slider
                value={[typeof input.advancedSettings?.poeticDensity === 'number' ? input.advancedSettings.poeticDensity : 5]}
                onValueChange={([value]) => handleChange('advancedSettings', {...input.advancedSettings, poeticDensity: value})}
                max={10}
                min={0}
                step={1}
              />
              <div className="flex justify-between text-xs text-muted-foreground">
                <span>シンプル</span>
                <span>バランス</span>
                <span>装飾的</span>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="narrativeStyle">物語スタイル</Label>
              <Select 
                value={input.advancedSettings?.narrativeStyle || 'auto'} 
                onValueChange={(value) => handleChange('advancedSettings', {...input.advancedSettings, narrativeStyle: value})}
              >
                <SelectTrigger id="narrativeStyle">
                  <SelectValue placeholder="物語スタイルを選択" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="auto">自動選択</SelectItem>
                  <SelectItem value="linear">線形</SelectItem>
                  <SelectItem value="flashback">フラッシュバック</SelectItem>
                  <SelectItem value="circular">循環的</SelectItem>
                  <SelectItem value="fragmented">断片的</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Separator />

            <div className="space-y-2">
              <Label>ターゲット層</Label>
              <div className="grid grid-cols-2 gap-2">
                <Select 
                  value={input.targetAudience?.ageGroup || 'auto'} 
                  onValueChange={(value) => handleChange('targetAudience', {...input.targetAudience, ageGroup: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="年齢層" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="kids">子供</SelectItem>
                    <SelectItem value="teen">ティーン</SelectItem>
                    <SelectItem value="young_adult">若年成人</SelectItem>
                    <SelectItem value="adult">成人</SelectItem>
                    <SelectItem value="all">全年齢</SelectItem>
                  </SelectContent>
                </Select>
                <Select 
                  value={input.targetAudience?.gender || 'auto'} 
                  onValueChange={(value) => handleChange('targetAudience', {...input.targetAudience, gender: value})}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="性別" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">自動選択</SelectItem>
                    <SelectItem value="male">男性向け</SelectItem>
                    <SelectItem value="female">女性向け</SelectItem>
                    <SelectItem value="neutral">中性的</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />

            <Card className="p-4 space-y-4 bg-muted/50">
              <h3 className="text-lg font-semibold">生成オプション</h3>
              
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label htmlFor="creativityLevel">創造性レベル</Label>
                  <span className="text-sm font-medium">
                    {typeof input.creativityLevel === 'number' ? input.creativityLevel : 'auto'}
                  </span>
                </div>
                <Slider
                  id="creativityLevel"
                  min={1}
                  max={10}
                  step={1}
                  value={[typeof input.creativityLevel === 'number' ? input.creativityLevel : 7]}
                  onValueChange={([value]) => handleChange('creativityLevel', value)}
                  className="w-full"
                />
                <p className="text-xs text-muted-foreground">
                  1: 保守的 ← → 10: 実験的
                </p>
              </div>
            </Card>
          </div>
        </ScrollArea>
      </TabsContent>
    </Tabs>
  )
}